import { QueryCache, QueryClient } from '@tanstack/react-query';

/**
 * SIL-667 (26,701 401s = 37% of all API requests, three unattended-tab
 * polling endpoints). This is the single place that decides "is this
 * failure an auth failure" — the retry policy, the refetchInterval
 * back-off, and the redirect-to-login trigger all read it, instead of each
 * re-deriving its own notion of "401" from ad hoc error shapes.
 *
 * `throwIfNotOk` (apps/customer/src/lib/api.ts, apps/operations/src/lib/api.ts)
 * attaches `status` to the thrown Error — that convention is what this
 * reads. A call site that throws a bare `Error('HTTP 401')` without a
 * `status` field won't be recognized here; keep using `throwIfNotOk` (or
 * attach `status` yourself) rather than hand-rolling error throwing.
 */
export function isAuthError(error: unknown): boolean {
  return error instanceof Error && (error as { status?: number }).status === 401;
}

/**
 * A 401 is never transient — retrying it just re-sends the same stale or
 * absent credential. `createAuthFetch` (packages/shared/src/auth/fetch.ts)
 * already attempts exactly one token refresh + retry at the fetch layer
 * before a 401 ever reaches here; a 401 that reaches the query layer means
 * that already failed, so further TanStack Query retries can only burn
 * Lambda concurrency (account cap: 10) for a guaranteed-doomed request.
 * Non-auth failures (network blips, 5xx) still get `maxRetries` attempts.
 */
export function createAuthAwareRetry(maxRetries: number) {
  return (failureCount: number, error: unknown): boolean => {
    if (isAuthError(error)) return false;
    return failureCount < maxRetries;
  };
}

/**
 * Wraps a fixed polling interval (`refetchInterval`) so it stops the moment
 * the query is failing on auth, instead of firing forever every N seconds
 * for as long as the tab stays open against an expired-or-absent token —
 * the exact shape of the flood this fixes (unread-count, opportunities
 * badge, import-batches all poll on a fixed interval with no auth check).
 * Polling resumes automatically once the query next succeeds, e.g. after
 * `onAuthExpired` redirects through login and the page remounts.
 */
export function authAwareRefetchInterval(intervalMs: number) {
  return (query: { state: { error: unknown } }): number | false =>
    isAuthError(query.state.error) ? false : intervalMs;
}

export interface CreateAppQueryClientOptions {
  /** Retry budget for non-auth failures. Defaults to 1 (matches prior apps/operations behavior). */
  retry?: number;
  staleTime?: number;
  gcTime?: number;
  refetchOnWindowFocus?: boolean;
  /**
   * Called at most once per redirect attempt, the first time any query in
   * this QueryClient observes a 401 that survived `createAuthFetch`'s own
   * refresh-and-retry — i.e. the session is genuinely gone, not a
   * transient race. Wire this to the app's `AuthClient.silentRefreshOrRedirect()`:
   * it silently refreshes if the refresh token is still good, else redirects
   * to the Cognito Hosted UI. This is what turns "stop polling" into "route
   * to re-authentication" per SIL-667, without teaching the fetch wrapper
   * itself to redirect (that stays each app's route-guard's job — see
   * packages/shared/src/auth/fetch.ts).
   */
  onAuthExpired: () => void | Promise<void>;
}

/**
 * Shared QueryClient factory for apps/customer and apps/operations. Centralizing
 * this means the auth-aware retry policy and the redirect-on-401 wiring live
 * in one place instead of being reimplemented (or forgotten) per app.
 */
export function createAppQueryClient(options: CreateAppQueryClientOptions): QueryClient {
  const {
    retry = 1,
    staleTime = 0,
    gcTime = 30 * 60 * 1000,
    refetchOnWindowFocus = false,
    onAuthExpired,
  } = options;

  // Dedupe concurrent onError firings (e.g. three polling queries all 401
  // in the same tick) into a single onAuthExpired() call in flight.
  let redirecting = false;

  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (!isAuthError(error) || redirecting) return;
        redirecting = true;
        void Promise.resolve(onAuthExpired()).finally(() => {
          redirecting = false;
        });
      },
    }),
    defaultOptions: {
      queries: {
        staleTime,
        gcTime,
        refetchOnWindowFocus,
        retry: createAuthAwareRetry(retry),
      },
    },
  });
}
